/**
 * VehicleSettingsModal.jsx — Configure Tessie integration:
 *   • Mileage rate ($/mi)
 *   • Grace period (days)
 *   • Sticky window (minutes) for driver suggestion
 *   • Driver map: Tessie driver_profile name → roster member
 *   • Re-run suggestions against existing drives
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Box, Typography, TextField, Button, Stack, Divider, Alert,
  Select, MenuItem, FormControl, InputLabel, Chip, Tooltip,
  List, ListItem, ListItemText, CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoFixHigh as ResuggestIcon,
  CloudSync as RemapIcon,
  Key as KeyIcon,
  PersonOff as NoKeyIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useMobile } from '../../hooks/useMobile';
import { useRoster } from '../../hooks/useRoster';
import { useRBAC } from '../../hooks/useRBAC';
import {
  useTessieConfig, useUpdateTessieConfig,
  useTeslaDrivers, useDriverProfilesSeen,
  useResuggest, useRemapDrivers,
} from '../../hooks/useTessie';

function rosterDotColor(roster, id) {
  return roster.find((r) => r.id === id)?.accentColor || '#9e9e9e';
}

export default function VehicleSettingsModal({ open, onClose }) {
  const { isMobile } = useMobile();
  const { isSuperAdmin } = useRBAC();
  const readOnly = !isSuperAdmin;
  const { data: config, isLoading: configLoading } = useTessieConfig();
  const { data: roster = [] } = useRoster();
  const { data: teslaDrivers = [], isLoading: driversLoading, error: driversError } = useTeslaDrivers({ enabled: open });
  const { data: seenProfiles = [] } = useDriverProfilesSeen({ enabled: open });
  const updateMut = useUpdateTessieConfig();
  const resuggestMut = useResuggest();
  const remapMut = useRemapDrivers();

  const [vehicleName, setVehicleName] = useState('');
  const [mileageRate, setMileageRate] = useState('0.75');
  const [graceDays, setGraceDays] = useState('14');
  const [stickyWindowMin, setStickyWindowMin] = useState('45');
  const [driverMap, setDriverMap] = useState({});
  const [newProfileName, setNewProfileName] = useState('');
  const [newRosterId, setNewRosterId] = useState('');
  const [saveError, setSaveError] = useState('');

  // Hydrate form from server
  useEffect(() => {
    if (!config) return;
    setVehicleName(config.vehicleName || '');
    setMileageRate(String(config.mileageRate ?? 0.75));
    setGraceDays(String(config.graceDays ?? 14));
    setStickyWindowMin(String(config.stickyWindowMin ?? 45));
    setDriverMap(config.driverMap || {});
  }, [config, open]);

  // Build the union of known profile names: from map + from Tessie drivers + from seen drives
  const candidateProfiles = useMemo(() => {
    const set = new Map(); // name → { source: 'tesla'|'seen'|'manual', meta }
    teslaDrivers.forEach((d) => {
      if (d.firstName) {
        set.set(d.firstName, { source: 'tesla', hasPhoneKey: d.hasPhoneKey, fullName: d.fullName });
      }
    });
    seenProfiles.forEach((p) => {
      if (p.profile && !set.has(p.profile)) {
        set.set(p.profile, { source: 'seen', driveCount: p.drive_count });
      }
    });
    Object.keys(driverMap).forEach((name) => {
      if (!set.has(name)) set.set(name, { source: 'manual' });
    });
    return Array.from(set.entries()).map(([name, meta]) => ({ name, ...meta }));
  }, [teslaDrivers, seenProfiles, driverMap]);

  const setMapping = (profileName, rosterId) => {
    setDriverMap((prev) => {
      const next = { ...prev };
      if (!rosterId) {
        delete next[profileName];
      } else {
        next[profileName] = rosterId;
      }
      return next;
    });
  };

  const addManualEntry = () => {
    const name = newProfileName.trim();
    if (!name || !newRosterId) return;
    setDriverMap((prev) => ({ ...prev, [name]: newRosterId }));
    setNewProfileName('');
    setNewRosterId('');
  };

  const removeEntry = (name) => {
    setDriverMap((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSave = async () => {
    setSaveError('');
    const rate = parseFloat(mileageRate);
    const grace = parseInt(graceDays, 10);
    const sticky = parseInt(stickyWindowMin, 10);
    if (isNaN(rate) || rate < 0) { setSaveError('Mileage rate must be a positive number'); return; }
    if (isNaN(grace) || grace < 1) { setSaveError('Grace days must be at least 1'); return; }
    if (isNaN(sticky) || sticky < 0) { setSaveError('Sticky window must be 0 or greater'); return; }
    try {
      await updateMut.mutateAsync({
        vehicleName: vehicleName.trim(),
        mileageRate: rate,
        graceDays: grace,
        stickyWindowMin: sticky,
        driverMap,
      });
    } catch (err) {
      setSaveError(err?.message || 'Failed to save');
    }
  };

  const handleSaveAndRemap = async () => {
    await handleSave();
    if (saveError) return;
    try {
      await remapMut.mutateAsync();
      await resuggestMut.mutateAsync();
    } catch {
      // errors already surfaced in the mutation state
    }
  };

  const dirty = useMemo(() => {
    if (!config) return false;
    if ((config.vehicleName || '') !== vehicleName.trim()) return true;
    if (String(config.mileageRate ?? 0.75) !== mileageRate) return true;
    if (String(config.graceDays ?? 14) !== graceDays) return true;
    if (String(config.stickyWindowMin ?? 45) !== stickyWindowMin) return true;
    if (JSON.stringify(config.driverMap || {}) !== JSON.stringify(driverMap)) return true;
    return false;
  }, [config, vehicleName, mileageRate, graceDays, stickyWindowMin, driverMap]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
      <DialogTitle sx={{ pr: 6 }}>
        Vehicle Settings
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {configLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {readOnly && (
              <Alert severity="info" variant="outlined">
                Only super-admins can change these settings.
              </Alert>
            )}
            {/* Connection status */}
            <Box>
              <Typography variant="overline" color="text.secondary">Connection</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  color={config?.configured ? 'success' : 'error'}
                  label={config?.configured ? 'Tessie configured' : 'Not configured'}
                  variant="outlined"
                />
                {config?.lastSyncAt && (
                  <Typography variant="caption" color="text.secondary">
                    Last sync: {new Date(config.lastSyncAt).toLocaleString()}
                  </Typography>
                )}
              </Stack>
            </Box>

            {/* Basic config */}
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
              <TextField
                label="Vehicle name"
                size="small"
                fullWidth
                value={vehicleName}
                onChange={(e) => setVehicleName(e.target.value)}
                placeholder="Roto Flight Tesla"
                disabled={readOnly}
              />
              <TextField
                label="Personal rate ($/mi)"
                size="small"
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                value={mileageRate}
                onChange={(e) => setMileageRate(e.target.value)}
                fullWidth
                disabled={readOnly}
              />
            </Stack>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
              <TextField
                label="Grace period (days)"
                size="small"
                type="number"
                inputProps={{ min: '1' }}
                value={graceDays}
                onChange={(e) => setGraceDays(e.target.value)}
                fullWidth
                helperText="Untagged drives auto-confirm as personal after this"
                disabled={readOnly}
              />
              <TextField
                label="Sticky window (min)"
                size="small"
                type="number"
                inputProps={{ min: '0' }}
                value={stickyWindowMin}
                onChange={(e) => setStickyWindowMin(e.target.value)}
                fullWidth
                helperText="Assume same driver for drives within this window"
                disabled={readOnly}
              />
            </Stack>

            <Divider />

            {/* Driver Map */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="overline" color="text.secondary">
                  Driver map · Tesla Driver Profile → Roster
                </Typography>
                <Tooltip title="Apply current map to existing unconfirmed drives">
                  <span>
                    <Button
                      size="small"
                      startIcon={<RemapIcon />}
                      onClick={() => remapMut.mutate()}
                      disabled={remapMut.isPending || dirty || readOnly}
                    >
                      {remapMut.isPending ? 'Remapping…' : 'Remap existing drives'}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>

              {dirty && (
                <Alert severity="info" sx={{ mb: 1 }} variant="outlined">
                  Save changes before remapping existing drives.
                </Alert>
              )}
              {remapMut.isSuccess && remapMut.data && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  Remapped {remapMut.data.updated} drive{remapMut.data.updated === 1 ? '' : 's'} using {remapMut.data.mappings} mapping{remapMut.data.mappings === 1 ? '' : 's'}.
                </Alert>
              )}

              {driversLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Fetching Tesla account holders…</Typography>
                </Box>
              )}
              {driversError && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Couldn't fetch Tesla drivers: {driversError.message}
                </Alert>
              )}

              {candidateProfiles.length === 0 && !driversLoading && (
                <Alert severity="info" variant="outlined">
                  No driver profiles detected yet. Set up Driver Profiles on the Tesla touchscreen and link each phone key — profile names will appear here after the next sync.
                </Alert>
              )}

              <List dense disablePadding>
                {candidateProfiles.map((p) => {
                  const rosterId = driverMap[p.name] || '';
                  return (
                    <ListItem
                      key={p.name}
                      sx={{ px: 0, gap: 1, flexWrap: 'wrap' }}
                      secondaryAction={
                        rosterId && !readOnly && (
                          <IconButton edge="end" size="small" onClick={() => removeEntry(p.name)} aria-label="Clear mapping">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )
                      }
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                            {p.source === 'tesla' && (
                              <Tooltip title={p.hasPhoneKey ? 'Phone key paired' : 'No phone key'}>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={p.hasPhoneKey ? 'success' : 'default'}
                                  icon={p.hasPhoneKey ? <KeyIcon sx={{ fontSize: 14 }} /> : <NoKeyIcon sx={{ fontSize: 14 }} />}
                                  label="Tesla"
                                />
                              </Tooltip>
                            )}
                            {p.source === 'seen' && (
                              <Chip size="small" variant="outlined" label={`${p.driveCount} drive${p.driveCount === 1 ? '' : 's'}`} />
                            )}
                            {p.source === 'manual' && <Chip size="small" label="manual" variant="outlined" />}
                          </Stack>
                        }
                        secondary={p.fullName && p.fullName !== p.name ? p.fullName : null}
                      />
                      <FormControl size="small" sx={{ minWidth: 180, mr: rosterId ? 4 : 0 }}>
                        <InputLabel>Roster</InputLabel>
                        <Select
                          label="Roster"
                          value={rosterId}
                          onChange={(e) => setMapping(p.name, e.target.value)}
                          disabled={readOnly}
                        >
                          <MenuItem value=""><em>Unassigned</em></MenuItem>
                          {roster.map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.accentColor || '#9e9e9e' }} />
                                <span>{r.name}</span>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </ListItem>
                  );
                })}
              </List>

              <Divider sx={{ my: 1.5 }} />

              {!readOnly && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Add a mapping manually (use if a profile name isn't listed yet):
                  </Typography>
                  <Stack direction={isMobile ? 'column' : 'row'} spacing={1}>
                    <TextField
                      size="small"
                      label="Driver profile name"
                      value={newProfileName}
                      onChange={(e) => setNewProfileName(e.target.value)}
                      placeholder="e.g. Jon"
                      fullWidth
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }} fullWidth={isMobile}>
                      <InputLabel>Roster</InputLabel>
                      <Select
                        label="Roster"
                        value={newRosterId}
                        onChange={(e) => setNewRosterId(e.target.value)}
                      >
                        <MenuItem value=""><em>Select…</em></MenuItem>
                        {roster.map((r) => (
                          <MenuItem key={r.id} value={r.id}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.accentColor || '#9e9e9e' }} />
                              <span>{r.name}</span>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addManualEntry}
                      disabled={!newProfileName.trim() || !newRosterId}
                    >
                      Add
                    </Button>
                  </Stack>
                </>
              )}
            </Box>

            <Divider />

            {/* Suggestion engine */}
            <Box>
              <Typography variant="overline" color="text.secondary">Driver suggestion engine</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Analyzes history (start/end locations + time-of-day + sticky windows) to guess who drove when the car isn't reporting a profile. Re-run after pairing drivers or bulk-classifying.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={resuggestMut.isPending ? <CircularProgress size={14} /> : <ResuggestIcon />}
                onClick={() => resuggestMut.mutate()}
                disabled={resuggestMut.isPending || readOnly}
              >
                {resuggestMut.isPending ? 'Analyzing…' : 'Re-run suggestions'}
              </Button>
              {resuggestMut.isSuccess && resuggestMut.data && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                  Scanned {resuggestMut.data.scanned} · suggested {resuggestMut.data.suggested}
                </Typography>
              )}
              {resuggestMut.isError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {resuggestMut.error?.message || 'Resuggest failed'}
                </Typography>
              )}
            </Box>

            {saveError && <Alert severity="error">{saveError}</Alert>}
            {updateMut.isSuccess && !dirty && (
              <Alert severity="success" variant="outlined">Settings saved.</Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
        <Box sx={{ flex: 1 }} />
        {!readOnly && (
          <>
            <Button
              onClick={handleSave}
              variant="outlined"
              disabled={!dirty || updateMut.isPending}
            >
              {updateMut.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              onClick={handleSaveAndRemap}
              variant="contained"
              startIcon={<LinkIcon />}
              disabled={updateMut.isPending || remapMut.isPending || resuggestMut.isPending}
            >
              Save + Remap + Resuggest
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
